<div class="vendor-page analysis-shell">
  <section class="vendor-shell hero-card">
    <div class="hero-row">
      <div class="title-block">
      <span class="title-icon">
        <lucide-icon [img]="BarChart3Icon" [size]="18"></lucide-icon>
      </span>
      <div>
        <h1 class="page-title-lite">Approved Contract List</h1>
        <p class="page-subtitle-lite">Only approved contracts are shown here. Actual Quantum is matched from the latest saved REA report using application or acceptance number.</p>
      </div>
    </div>

    <div class="hero-actions">
      <button type="button" class="ghost-btn create-btn export" (click)="refresh()" [disabled]="loading()">
        <lucide-icon [img]="RefreshCcwIcon" [size]="14"></lucide-icon>
        <span>{{ loading() ? 'Refreshing...' : 'Refresh' }}</span>
      </button>
    </div>
    </div>
  </section>

  @if (loading()) {
    <section class="panel-card empty-state">Loading approved contract list...</section>
  } @else {
    <div class="metrics-grid row g-3 mt-1">
      <article class="col-12 col-md-6 col-xl">
        <div class="metric-card summary-card h-100">
          <div class="metric-head summary-card-header">
            <span class="metric-label summary-label">Approved Contracts</span>
            <lucide-icon [img]="BarChart3Icon" [size]="18"></lucide-icon>
          </div>
          <strong class="summary-value">{{ totalApprovedContracts() }}</strong>
          <div class="summary-track">
            <div class="summary-segment approved" style="width: 100%"></div>
          </div>
          <small>Total approved and active contracts</small>
        </div>
      </article>
      <article class="col-12 col-md-6 col-xl">
        <div class="metric-card summary-card h-100">
          <div class="metric-head summary-card-header">
            <span class="metric-label summary-label">Sale Purchase</span>
            <lucide-icon [img]="BarChart3Icon" [size]="18"></lucide-icon>
          </div>
          <strong class="summary-value">{{ totalSaleContracts() }}</strong>
          <div class="summary-track">
            <div class="summary-segment pending" style="width: 100%"></div>
          </div>
          <small>Sale and purchase contracts</small>
        </div>
      </article>
      <article class="col-12 col-md-6 col-xl">
        <div class="metric-card summary-card h-100">
          <div class="metric-head summary-card-header">
            <span class="metric-label summary-label">Banking</span>
            <lucide-icon [img]="BarChart3Icon" [size]="18"></lucide-icon>
          </div>
          <strong class="summary-value">{{ totalBankingContracts() }}</strong>
          <div class="summary-track">
            <div class="summary-segment neutral" style="width: 100%"></div>
          </div>
          <small>Banking contracts</small>
        </div>
      </article>
    </div>

    <section class="panel-card table-panel p-3 p-lg-4">
      <div class="section-head">
        <div>
          <h2>Approved Contract Table</h2>
          <p>Seller, buyer, approved, and REA actual quantum details are shown here.</p>
        </div>
      </div>

      @if (approvedContracts().length === 0) {
        <div class="empty-state">No approved contracts are available.</div>
      } @else {
        <div class="table-wrap">
          <div class="table-responsive">
            <table class="contracts-table">
              <thead>
                <tr>
                  <th class="col-serial">S.No.</th>
                  <th class="col-contract">Contract</th>
                  <th class="col-side">Seller Side</th>
                  <th class="col-side">Buyer Side</th>
                  <th class="col-quantum">Punched Quantum (MWh)</th>
                  <th class="col-quantum">Approved Quantum (MWh)</th>
                  <th class="col-quantum">Actual Quantum (REA MWh)</th>
                  <th class="col-deviation">Deviation</th>
                  <th class="col-financial">Profit / Loss</th>
                  <th class="col-financial">Penalty</th>
                  <th class="col-period">Period</th>
                  <th class="col-status">Status</th>
                </tr>
              </thead>
              <tbody>
                @for (row of approvedContracts(); track row.contractId; let index = $index) {
                  <tr>
                    <td class="serial-cell">{{ index + 1 }}</td>
                    <td>
                      <div class="info-stack contract-stack">
                        <div class="primary-cell">{{ row.contractNumber }}</div>
                        <div class="meta-row">
                          <span class="meta-pill">{{ row.contractType }}</span>
                          <span class="meta-pill muted">{{ row.contractCategory }}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div class="info-stack side-stack">
                        <div class="party-name">{{ row.sellerName }}</div>
                        <div class="sub-line">LOA: {{ row.sellerLoa }}</div>
                        <div class="sub-line">Quantum: <strong>{{ formatQuantum(row.sellerQuantum) }}</strong></div>
                      </div>
                    </td>
                    <td>
                      <div class="info-stack side-stack">
                        <div class="party-name">{{ row.buyerName }}</div>
                        <div class="sub-line">LOA: {{ row.buyerLoa }}</div>
                        <div class="sub-line">Quantum: <strong>{{ formatQuantum(row.buyerQuantum) }}</strong></div>
                      </div>
                    </td>
                    <td class="quantum-cell">{{ formatQuantum(row.punchedQuantum) }}</td>
                    <td class="quantum-cell">{{ formatQuantum(row.approvedQuantum) }}</td>
                    <td class="quantum-cell">{{ formatQuantum(row.actualQuantum) }}</td>
                    <td>
                      <div class="deviation-cell" [class.deviation-cell-positive]="deviationDirection(row) === 'increase'" [class.deviation-cell-negative]="deviationDirection(row) === 'decrease'">
                        <div class="deviation-value-row">
                          <span class="deviation-arrow">{{ deviationDirection(row) === 'increase' ? '▲' : deviationDirection(row) === 'decrease' ? '▼' : '•' }}</span>
                          <span class="deviation-value">{{ formatDeviation(row) }}</span>
                        </div>
                        <div class="deviation-track">
                          <span class="deviation-bar" [style.width.%]="deviationBarWidth(row)"></span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div class="financial-cell">
                        <span [class]="profitLossClass(row)">{{ profitLossLabel(row) }}</span>
                      </div>
                    </td>
                    <td>
                      <div class="financial-cell penalty-cell">
                        <span class="metric-chip metric-chip-warning">{{ penaltySummary(row) }}</span>
                        <small>{{ penaltyDetail(row) }}</small>
                      </div>
                    </td>
                    <td>
                      <div class="info-stack period-stack">
                        <div>{{ formatDate(row.periodFrom) }}</div>
                        <div class="period-separator">to</div>
                        <div>{{ formatDate(row.periodTo) }}</div>
                      </div>
                    </td>
                    <td><span [class]="statusClass(row.status)">{{ row.status }}</span></td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      }
    </section>
  }
</div>
